import { UpsertForm } from "@/components/domain";
import { Field } from "@/components/field";
import { LoadingSpinner } from "@/components/loading-spinner";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useSyncLoading, useSyncTitle } from "@/context/sheet";
import {
  useCreateAccommodationFulfillment,
  useGetAccommodationsWithFulfillments,
} from "@/hooks/accommodations";
import { useGetClient } from "@/hooks/clients";
import { useGetHotelRoom } from "@/hooks/hotel-rooms";
import { useGetReservationDays } from "@/hooks/reservation-days";
import { useGetAllReservationServicesWithfulfillments } from "@/hooks/reservation-services";
import { useCreateReservationServiceFulfillment } from "@/hooks/reservation-services/update.hooks";
import { useGetReservation } from "@/hooks/reservations";
import { useGetServiceProvider } from "@/hooks/service-providers/get.hooks";
import { useGetService } from "@/hooks/services";
import { useGetTourAgency } from "@/hooks/tour-agencies";
import { T } from "@/i18n";
import {
  Accommodation,
  AccommodationWithFulfillments,
  ReservationServiceWithFulfillments,
  CreateFulfillment,
  FulfillmentState,
} from "@/models";
import { ScheduleManager } from "@/routes/(reservations)/-managers";
import { generateReservationLabel } from "@/routes/(reservations)/-utils";
import { useForm } from "@deadcow-enterprises/react-forms";
import { Coins } from "lucide-react";
import { useMemo, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";

export function ReservationFulfillments({
  id,
  domain = "both",
}: {
  id: string;
  domain?: "service" | "hotel" | "both";
}) {
  const { data: accommodations, isLoading: accommodationsLoading } =
    useGetAccommodationsWithFulfillments(id);
  const { data: services, isLoading: servicesLoading } =
    useGetAllReservationServicesWithfulfillments(id);
  const { data: reservationDays } = useGetReservationDays(id);

  const { data: reservation, isLoading: reservationLoading } =
    useGetReservation(id);
  const { data: tourAgency } = useGetTourAgency(
    "tour",
    reservation?.tourAgencyId?.toString() ?? "",
    {
      enabled: typeof reservation?.tourAgencyId === "number",
    },
  );

  const { data: groupLeader } = useGetClient(
    reservation?.groupLeaderId?.toString() ?? "",
    {
      enabled: typeof reservation?.groupLeaderId === "number",
    },
  );

  useSyncLoading(reservationLoading);
  useSyncTitle(
    (!reservation
      ? undefined
      : generateReservationLabel(reservation, tourAgency, groupLeader)) ?? "-",
  );

  const days = useMemo(() => {
    const scheduleManager = new ScheduleManager(
      id,
      (accommodations ?? []) as unknown as Accommodation[],
      services ?? [],
      reservationDays ?? [],
    );
    return scheduleManager.days;
  }, [accommodations, id, reservationDays, services]);

  return (
    <div className="mt-3 grid gap-5">
      {days.map((day) => {
        const dayNumber = day.reservationDay.value.day;
        const dayName = day.reservationDay.value.name ?? "";
        const services = day.services.map((s) => s.value);
        const accommodations = day.accommodations.map((a) => a.value);
        return (
          <div className="grid gap-3" key={dayNumber}>
            <span className="text-primary- font-medium">
              Day {dayNumber} {dayName}
            </span>
            {(domain === "both" || domain === "hotel") && (
              <div className="grid gap-2">
                <span className="text-base text-muted-foreground">
                  <T tKey="reservations.fulfillments.accommodations" />
                </span>
                {accommodationsLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton className="h-5 w-full" key={i} />
                    ))
                  : accommodations.map((acc) => (
                      <AccommodationView
                        key={acc.id}
                        accommodation={
                          acc as unknown as AccommodationWithFulfillments
                        }
                      />
                    ))}
              </div>
            )}
            {(domain === "both" || domain === "service") && (
              <div className="grid gap-2">
                <span className="text-base text-muted-foreground">
                  <T tKey="reservations.fulfillments.services" />
                </span>
                {servicesLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton className="h-5 w-full" key={i} />
                    ))
                  : services.map((serv) => (
                      <ServiceView
                        key={serv.id}
                        service={
                          serv as unknown as ReservationServiceWithFulfillments
                        }
                      />
                    ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AccommodationView(props: {
  accommodation: AccommodationWithFulfillments;
}) {
  const [open, setOpen] = useState(false);
  const { mutateAsync } = useCreateAccommodationFulfillment({
    onSuccess: () => {
      setOpen(false);
    },
  });
  const { data: hotelRoom, isLoading } = useGetHotelRoom(
    props.accommodation.hotelRoom?.hotelId?.toString() ?? "",
    props.accommodation.hotelRoomId?.toString() ?? "",
    { enabled: typeof props.accommodation.hotelRoomId === "number" },
  );
  const hasBallance = useMemo(
    () => (props.accommodation.balance ?? 0) > 0,
    [props.accommodation.balance],
  );
  return (
    <>
      <ResponsiveDialog open={open} onClose={() => setOpen(false)}>
        <CreateFulfillmentForm
          onCreate={async (f) => {
            await mutateAsync({
              fulfillment: f,
              accommodation: props.accommodation,
            });
          }}
          balance={props.accommodation.balance}
          total={props.accommodation.hotelRoomPrice?.price}
          title={`Day ${props.accommodation.day} ${hotelRoom?.hotelName ?? "-"} (${hotelRoom?.designation ?? "-"})`}
        />
      </ResponsiveDialog>
      <div className="flex items-center gap-2">
        {hasBallance ? (
          <Button onClick={() => setOpen(true)} variant="text">
            <Coins />
          </Button>
        ) : (
          <> </>
        )}
        {isLoading ? (
          <Skeleton className="h-6 w-full" />
        ) : (
          `${hotelRoom?.hotelName ?? "-"} (${hotelRoom?.designation ?? "-"})`
        )}
      </div>
    </>
  );
}

function ServiceView({
  service,
}: {
  service: ReservationServiceWithFulfillments;
}) {
  const [open, setOpen] = useState(false);
  const { mutateAsync } = useCreateReservationServiceFulfillment({
    onSuccess: () => {
      setOpen(false);
    },
  });
  const { data: serviceDetail, isLoading } = useGetService(
    service.serviceId?.toString() ?? "",
    {
      enabled: typeof service.serviceId === "number",
    },
  );
  const hasServiceProvider = useMemo(
    () => typeof service.serviceProviderId === "number",
    [service.serviceProviderId],
  );
  const { data: serviceProvider, isLoading: serviceProviderLoading } =
    useGetServiceProvider(
      "service",
      service.serviceProviderId?.toString() ?? "",
      {
        enabled: hasServiceProvider,
      },
    );
  const hasBallance = useMemo(
    () => (service.balance ?? 0) > 0,
    [service.balance],
  );
  return (
    <>
      <ResponsiveDialog open={open} onClose={() => setOpen(false)}>
        <CreateFulfillmentForm
          onCreate={async (f) => {
            await mutateAsync({
              fulfillment: f,
              service,
            });
          }}
          balance={service.balance}
          total={service.price}
          title={`Day ${service.day} ${serviceDetail?.name ?? "-"}`}
        >
          {hasServiceProvider ? (
            <Field
              id="service-provider"
              label="reservations.fulfillments.fields.serviceProvider"
            >
              {serviceProviderLoading || !serviceProvider ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <SelectSearch
                  id="service-provider"
                  onChange={() => void 0}
                  disabled
                  value={serviceProvider.providerId}
                  options={[
                    {
                      label: serviceProvider.provider?.name ?? "-",
                      value: serviceProvider.providerId,
                    },
                  ]}
                />
              )}
            </Field>
          ) : (
            <></>
          )}
        </CreateFulfillmentForm>
      </ResponsiveDialog>
      <div className="flex items-center gap-2">
        {hasBallance ? (
          <Button onClick={() => setOpen(true)} variant="text">
            <Coins />
          </Button>
        ) : (
          <> </>
        )}
        {isLoading ? <Skeleton className="h-6 w-full" /> : serviceDetail?.name}
      </div>
    </>
  );
}

function CreateFulfillmentForm({
  title,
  children,
  dueDate,
  balance,
  total,
  onCreate,
}: {
  title: string;
  children?: ReactNode;
  dueDate?: Date;
  balance?: number | null;
  total?: number | null;
  onCreate: (fulfillment: CreateFulfillment) => Promise<void>;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<CreateFulfillment>({
    state: {
      value: FulfillmentState.NotPayed,
    },
    value: {
      value: undefined,
    },
    agencyCharge: {
      value: undefined,
    },
    dueDate: {
      value: dueDate,
    },
    expenses: {
      value: undefined,
    },
    invoiceNumber: {
      value: undefined,
    },
  });
  return (
    <div className="grid">
      <div className="mb-4 grid gap-2">
        <span className="text-lg font-semibold">
          <T tKey="reservations.fulfillments.new.title" />
        </span>
        <span className="text-sm text-muted-foreground">{title}</span>
      </div>
      {children}
      <UpsertForm
        form={formInstance}
        onUpsert={onCreate}
        fields={[
          {
            key: "value",
            label: (
              <>
                {" "}
                {t("reservations.fulfillments.fields.value")}
                <span className="ml-2 text-muted-foreground">
                  <T
                    tKey="reservations.fulfillments.fields.balance"
                    args={{ balance: balance?.toString() ?? "-" }}
                  />
                  <T
                    tKey="reservations.fulfillments.fields.total"
                    args={{ total: total?.toString() ?? "-" }}
                  />
                </span>
              </>
            ),
            input: (form, id) => (
              <Input
                autoFocus
                id={id}
                type="number"
                step={0.01}
                min={0}
                max={balance ?? undefined}
                value={form.value.value ?? ""}
                onChange={(e) => {
                  form.setValue("value", Number.parseFloat(e.target.value));
                }}
              />
            ),
          },
          {
            key: "dueDate",
            label: "reservations.fulfillments.fields.dueDate",
            input: (form, id) => (
              <DatePicker
                id={id}
                className="w-full"
                date={form.value.dueDate ?? undefined}
                setDate={(d) => {
                  form.setValue("dueDate", d);
                }}
              />
            ),
          },
          {
            key: "invoiceNumber",
            label: "reservations.fulfillments.fields.invoiceNumber",
            input: (form, id) => (
              <Input
                id={id}
                value={form.value.invoiceNumber ?? ""}
                onChange={(e) => {
                  form.setValue("invoiceNumber", e.target.value);
                }}
              />
            ),
          },
        ]}
      >
        <div className="mt-4 flex w-full justify-end">
          <Button>
            {formInstance.loading ? (
              <LoadingSpinner />
            ) : (
              <T tKey="reservations.fulfillments.new.button" />
            )}
          </Button>
        </div>
      </UpsertForm>
    </div>
  );
}
